import axios from 'axios';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { T_Notification } from 'types/app.type';

import { NotificationService } from '@/services/Notification/service';

interface useNotificationProps {
  notifAgent: string;
  isSignedIn?: boolean;
  domain: string;
  specialHandler?: string;
}

const MAX_VISIBLE_NOTIFICATIONS = 3;

const useNotification = ({
  notifAgent,
  isSignedIn,
  domain,
  specialHandler,
}: useNotificationProps) => {
  const [cookies, setCookie] = useCookies(['notification']);
  const [notifications, setNotifications] = useState<T_Notification[]>([]);
  let [dismissedNotifications, setDismissedNotifications] = useState<string>(
    cookies.notification ?? ''
  ); // set the dismissedNotifications from localStorage

  const location = useLocation();
  let notificationService = new NotificationService(axios.create({ baseURL: notifAgent }));

  // Get Notifications and set the notifications after Filtering them based on the user state (signed in or out). Scope = ["loggedIn", "loggedOut", "global"]
  useEffect(() => {
    notificationService
      .getNotifications()
      .then((result: any) => setNotifications(filterNotifications(result)));
    //eslint-disable-next-line
  }, [location]);

  const getNormalisedPage = (page: string) => {
    if (!page) return null;

    try {
      const url = new URL(page, window.location.origin);
      const pathname = url.pathname.replace(/\/+$/, '') || '/';

      return `${url.host}${pathname}`;
    } catch {
      return null;
    }
  };

  // Check whether the current page is the target of the notification
  let isTargetPage = (pages: string[] = []) => {
    const currentPage = getNormalisedPage(window.location.href);
    return pages.some(page => getNormalisedPage(page) === currentPage);
  };
  let isExcludedPage = (pages: string[] = []) => {
    const currentPage = getNormalisedPage(window.location.href);
    return pages.filter(Boolean).some(page => getNormalisedPage(page) === currentPage);
  };

  // Filter Notifications based on dissmised notifications, user state, special scenarios handler and page target
  let filterNotifications = (notifications: T_Notification[] = []) => {
    return notifications
      .filter(notif => !dismissedNotifications?.includes(notif.id)) // Filter notifications that are not dismissed
      .filter(notif => notif.scope !== (isSignedIn ? 'loggedOut' : 'loggedIn')) // Filter notifications based on the state of the user
      .filter(notif => (notif.specialHandler ? notif.specialHandler === specialHandler : notif)) // Filter notifications that matches the special handler (if not, just return the notification)
      .filter(notif => (notif.pageTarget?.[0] ? isTargetPage(notif.pageTarget) : notif))
      .filter(notif => !isExcludedPage(notif.pageExclude))
      .slice(0, MAX_VISIBLE_NOTIFICATIONS);
  };

  // Dismiss the notification
  let dismissNotification = (id: string) => {
    dismissedNotifications =
      dismissedNotifications !== null ? dismissedNotifications.concat(id) : id;
    setDismissedNotifications(dismissedNotifications);
  };

  // Dismiss the notification
  let engageNotification = (id: string, url: string) => {
    window.location.href = url;
  };

  useEffect(() => {
    if (!dismissedNotifications) {
      return;
    }

    // Filter notifications
    setNotifications(filterNotifications(notifications));

    // Determine the domain based on the environment
    const isLocalhost = window.location.hostname === 'localhost';

    // If running on localhost, don't set a domain, else set the parent domain for production
    const cookieDomain = isLocalhost ? undefined : '.sky.co.nz';

    setCookie('notification', dismissedNotifications, { domain: cookieDomain });

    //eslint-disable-next-line
  }, [dismissedNotifications]);

  return { notifications, dismissNotification, dismissedNotifications, engageNotification };
};

export { useNotification };
